import * as React from 'react';
import cx from 'classnames';
import { find, isArray, isUndefined, map, sortBy } from 'lodash';

import {
  IToastRefHandles,
  Toast,
} from 'src/widgets/Toast';
import { IProspect } from 'src/common/models/member';
import { ICommunity } from 'src/common/models/community';
import { IFetchResult } from 'src/utils/hooks/useGet';
import { getErrorMessageFromResponse, IAPIErrorResponse } from 'src/common/utils/getErrorMessageFromResponse';

import { CheckIcon, SpinnerIcon } from 'src/icons';
import { ChevronDownIcon } from 'src/icons/ChevronDown';
import { HeartIcon } from 'src/icons/HeartIcon';
import { HeartFilledIcon } from 'src/icons/colored/HeartFilledIcon';
import { IOption, Select } from 'src/widgets/Select';

import { SubmitButton } from '../Button';

import { useInvite } from './hooks';
import { useGroupsForSocialAccounts } from './hooks/useGroupsForSocialAccounts';
import { getSocialAccountsFromProspects } from './utils';

import styles from './Favorite.scss';

const { useCallback, useEffect, useMemo, useRef, useState } = React;

export const MY_FAVORITES_TITLE = 'My Favorites';

export interface IFavoriteProps {
  className?: string;
  prospect: IProspect | IProspect[];
  preloadProspects?: IProspect | IProspect[];
  toastRef?: React.RefObject<IToastRefHandles>;
  onInvite?: (prospect: IProspect | IProspect[], group: ICommunity) => void;
}

export const Favorite: React.FC<IFavoriteProps> = (props) => {
  const {
    preloadProspects = props.prospect,
    prospect,
    className,
    toastRef: toastRefProp,
    onInvite,
  } = props;

  const heartRef = useRef();

  const [isPopoverShown, setIsPopoverShown] = useState(false);
  const toastRef = useRef<IToastRefHandles>(toastRefProp ? toastRefProp.current : null);

  const socialAccounts = isArray(prospect)
    ? getSocialAccountsFromProspects(...prospect)
    : getSocialAccountsFromProspects(prospect);

  const {
    isFetching,
    groups: invitedGroups,
    error,
    isAdding,
    addToGroup,
    isRemoving,
    removeFromGroup,
    targetGroupId,
  } = useGroupsForSocialAccounts({
    socialAccounts
  });

  const {
    communities: groups = []
  } = useInvite({
    prospect,
    preloadProspects
  });

  // We need to sort groups so My Favorites stays up
  const sortedGroups = sortBy(groups, (group) => (
    group.title === MY_FAVORITES_TITLE
      ? -1
      : group.id
  ));

  const myFavoritesGroup: ICommunity = find(sortedGroups, (group) =>
    group.title === MY_FAVORITES_TITLE
  );

  const isGroupAdded = (group: ICommunity) => {
    if (isUndefined(group)) {
      return false;
    }

    return !isUndefined(
      find(invitedGroups, ['id', group.id])
    );
  };

  const isSubmitting = isAdding || isRemoving;

  const getGroupOption = (group: ICommunity): IOption => {
    const isAdded = isGroupAdded(group);

    const { title, id } = group;
    const actions = [];

    if (
      isSubmitting 
        && group.id === targetGroupId
    ) {
      actions.push(
        <div
          className={styles.spinner}
        >
          <SpinnerIcon
            fill="#ffffff"
            size={18}
          />
        </div>
      );
    } else if (isAdded) {
      actions.push(
        <div
          className={styles.groupOptionAction}
        >
          <div className={styles.groupOptionActionLabel}>
            <CheckIcon
              size={8}
            />
            <span>Added</span>
          </div>
          <SubmitButton
            className={cx(
              styles.inviteButton,
              styles.toggleButton,
            )}
            key='inviteButton'
            theme='danger'
            label='Remove'
            submittingLabel=''
            isSubmitting={false}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              toggleGroup(group);
            }}
          />
        </div>
      );
    } else {
      actions.push(
        <div
          className={styles.groupOptionAction}
        >
          <SubmitButton
            className={cx(
              styles.inviteButton,
              styles.toggleButton,
              styles.addButton,
            )}
            key='inviteButton'
            label='Add'
            submittingLabel=''
            isSubmitting={false}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              toggleGroup(group);
            }}
          />
        </div>
      );
    }

    return {
      label: title,
      value: id,
      optionClass: cx(
        styles.option,
        styles.programOption,
        styles.groupOption,
        {
          [styles.added]: isAdded
        }
      ),
      showActions: true,
      actions,
    };
  };

  const groupOptions = useMemo(() => {
    const options: IOption[] = [];

    options.push({
      value: null,
      label: 'Add to your groups',
      optionClass: cx(styles.option, styles.optionTitle, styles.noSelect),
    });

    const mappedOptions = map(sortedGroups, (group) => getGroupOption(group));
    options.push(...mappedOptions);

    return options;
  }, [groups, invitedGroups, isSubmitting]);

  const add = async (group: ICommunity) => {
    const { id } = group;

    const json = await addToGroup(id) as IFetchResult<any>;

    if (!(
      json.status
        && json.status.code === 200
    )) {
      toastRef?.current?.showMessage({
        type: 'error',
        content: getErrorMessageFromResponse(json as IAPIErrorResponse),
      });
    } else {
      const message = group.title === MY_FAVORITES_TITLE
        ? 'Added to My Favorites'
        : `Added to Group "${group.title}"`;

      toastRef?.current?.showMessage({
        type: 'success',
        content: message,
      });

      onInvite?.(prospect, group);
    }
  };

  const remove = async (group: ICommunity) => {
    const { id } = group;

    const json = await removeFromGroup(id) as IFetchResult<any>;

    if (!(
      json.status
        && json.status.code === 200
    )) {
      toastRef.current.showMessage({
        type: 'error',
        content: getErrorMessageFromResponse(json as IAPIErrorResponse),
      });
    } else {
      const message = group.title === MY_FAVORITES_TITLE
        ? 'Removed from My Favorites'
        : `Removed from Group "${group.title}"`;

      toastRef.current.showMessage({
        type: 'success',
        content: message,
      });
    }
  };

  const isMyFavoritesAdded = useMemo(() => (
    isGroupAdded(myFavoritesGroup)
  ), [invitedGroups, myFavoritesGroup]);

  const toggleGroup = (group: ICommunity) => {
    const isAdded = isGroupAdded(group);

    isAdded ? remove(group) : add(group);
  };

  const handleHeartClick = useCallback(() => {
    toggleGroup(myFavoritesGroup);
  }, [myFavoritesGroup, invitedGroups]);

  const renderHeart = () => {
    const iconSize = 18;

    const icon = !isMyFavoritesAdded
      ? <HeartIcon size={iconSize} />
      : <HeartFilledIcon size={iconSize} />;

    return (
      <>
        <div
          ref={heartRef}
          className={cx(
            styles.FavoriteButton,
            styles.HeartButton,
          )}
          onClick={handleHeartClick}
        >
          {icon}
        </div>
      </>
    );
  };

  const renderDropdown = () => {
    return (
      <div
        className={cx(
          styles.FavoriteButton,
          styles.PopoverButton,
          {
            [styles.active]: isPopoverShown,
          }
        )}
      >
        <Select
          onChange={() => null}
          customLabelElement={<ChevronDownIcon />}
          options={groupOptions}
          className={styles.select}
          onMenuOpen={() => setIsPopoverShown(true)}
          onMenuClose={() => setIsPopoverShown(false)}
          popoverProps={{
            className: cx(styles.popover),
            contentWrapperClassName: styles.popoverContentWrapper,
            contentClassName: styles.popoverContent,
            anchorOrigin: 'middle',
            showArrow: false,
            shadowSize: 'large',
            minWidth: styles.popoverWidth,
            maxWidth: styles.popoverWidth,
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    if (error) {
      toastRef.current.showMessage({
        type: 'error',
        content: error.toString(),
      });
    }
  }, [error]);

  const isLoading = isFetching
    || isAdding
    || isRemoving;

  const isDisabled = !socialAccounts
    || !socialAccounts.length;

  return (
    <div
      className={cx(
        styles.Favorite,
        className,
        {
          [styles.isDisabled]: isDisabled,
          [styles.isLoading]: isLoading,
        }
      )}
    >
      {renderHeart()}
      {renderDropdown()}
      {!toastRefProp ? <Toast ref={toastRef} /> : null}
    </div>
  );
};
